import {buildUrl} from '../../utils/url'
import {BaseUrlCheckoutParams} from '../../utils/url/types/UrlParams'

export enum OfferCheckedIn {
  AP_OFFER_CLICK = 'ap_offer_click',
  OFFER_CHECK = 'offer_check'
}

export interface OfferCheckUrlParams
  extends Pick<
    BaseUrlCheckoutParams,
    | 'anonymousId'
    | 'currency'
    | 'deviceType'
    | 'userCountry'
    | 'redirectId'
    | 'searchId'
    | 'offerId'
    | 'oid'
    | 'pc'
    | 'offer_token'
    | 'recheckMetadataId'
    | 'bm'
    | 'mbm'
    | 'sg'
    | 'nc'
    | 'exp'
    | 'locale'
    | 'bundleId'
    | 'rl'
  > {
  offerCheckedIn?: OfferCheckedIn
  label?: string
}

/**
 * Builds the offer check endpoint URL based on the provided parameters.
 * Filters out undefined values and constructs the correct API path.
 * @param urlParams - Parameters required to build the offer check URL.
 * @param BoFhApi - Base URL for the API.
 * @returns The constructed offer check URL or null if required parameters are missing.
 * @example
 * buildOfferCheckEndpoint({ searchId: '123', offer_token: 'abc' }, 'https://api.example.com/')
 * // 'https://api.example.com/offers/abc/check?searchId=123'
 */
export const buildOfferCheckEndpoint = (
  urlParams: OfferCheckUrlParams,
  BoFhApi: string
) => {
  const {
    anonymousId,
    currency,
    deviceType,
    userCountry,
    redirectId,
    searchId,
    offerCheckedIn,
    pc,
    offer_token,
    recheckMetadataId,
    bm,
    mbm,
    sg,
    nc,
    exp,
    label,
    locale,
    bundleId,
    offerId,
    rl
  } = urlParams

  const pcValue = pc === 'f' || pc === 't' ? pc : undefined
  const hasSplitBookingParams = Boolean(bundleId)

  const searchParams: Record<string, string | undefined | null> = {
    anonymousId,
    currency,
    bundleId,
    deviceType,
    userCountry,
    redirectId,
    searchId,
    offerCheckedIn,
    recheckMetadataId,
    bm,
    mbm,
    sg,
    nc,
    exp,
    pc: pcValue,
    label,
    locale,
    rl,
    ...(!bundleId ? {sapiOfferId: offerId} : {})
  }

  if (!offer_token && !hasSplitBookingParams) {
    return null
  }

  const filteredSearchParams = Object.fromEntries(
    Object.entries(searchParams).filter(([, value]) => value !== undefined)
  )

  if (hasSplitBookingParams) {
    return buildUrl(
      `${BoFhApi}bundles/${bundleId}/check`,
      filteredSearchParams,
      {
        arrayFormat: 'indices'
      }
    )
  }

  return buildUrl(`${BoFhApi}offers/${offer_token}/check`, filteredSearchParams)
}
